import { z } from "zod";

import type { Product } from "@/types";

const availableSampleSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.url(),
});

const unavailableSampleSchema = z.union([
  z.null(),
  z.object({
    id: z.null(),
    filename: z.null(),
    url: z.null(),
  }),
]);

const createdAtSchema = z
  .union([
    z.iso.datetime({ offset: true }),
    z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
  ])
  .transform(
    (created) =>
      new Date(
        created.includes("T") ? created : `${created.replace(" ", "T")}Z`,
      ),
  );

export const productResponseSchema = z
  .object({
    Product: z.object({
      id: z.string(),
      name: z.string(),
      description_html: z.string(),
      price: z.coerce.number(),
      currency_code: z.string(),
      in_app_can_consume: z.boolean(),
      in_app_for_sale: z.boolean(),
      created: createdAtSchema,
    }),
    Author: z.array(z.object({ name: z.string() })),
    Image: z.object({
      url_mindlyapp_thumb: z.url(),
      url_mindlyapp_image: z.url(),
    }),
    Sample: z.union([availableSampleSchema, unavailableSampleSchema]),
    ProductFile: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        filetype: z.string(),
        filesize: z.coerce.number(),
        url: z.url(),
      }),
    ),
  })
  .transform<Product>((data) => {
    const authors = data.Author.map(({ name }) => name);
    const artistName = authors.join(", ");
    const sample = data.Sample?.id
      ? {
          id: data.Sample.id,
          trackName: data.Sample.filename,
          artistName,
          collectionName: data.Product.name,
          audioUrl: data.Sample.url,
          artworkUrl: data.Image.url_mindlyapp_image,
        }
      : undefined;

    return {
      id: data.Product.id,
      name: data.Product.name,
      authors,
      description: data.Product.description_html,
      image: {
        thumbnail: data.Image.url_mindlyapp_thumb,
        full: data.Image.url_mindlyapp_image,
      },
      price: {
        amount: data.Product.price / 100,
        currency: data.Product.currency_code,
      },
      sample,
      tracks: data.ProductFile.filter(
        ({ filetype }) => filetype === "audio",
      ).map((file) => ({
        id: file.id,
        trackName: file.name,
        artistName,
        collectionName: data.Product.name,
        audioUrl: file.url,
        artworkUrl: data.Image.url_mindlyapp_image,
      })),
      canPurchase: data.Product.in_app_for_sale,
      canPlay: data.Product.in_app_can_consume,
      createdAt: data.Product.created,
    };
  });

export const productsResponseSchema = z
  .object({ data: z.array(productResponseSchema) })
  .transform(({ data }) => data);
